import { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/api';

/**
 * useDivineBlink — drives the Divine flicker chain per user spec.
 *
 * Rules:
 *   • Poll /api/divine/worship-status every 20s (fine-grained enough for the
 *     2-min flicker window).
 *   • Each worship note has its own `scheduled_time`. Backend computes
 *     `needs_flicker=true` iff current_time is inside
 *     [scheduled_time, scheduled_time + 120s] AND not fused today.
 *   • Smooth continuous CSS pulse (no ON/OFF cadence).
 *   • After 2 min → backend returns needs_flicker=false → hook auto-stops.
 *   • Fires custom events for other components:
 *       - `divine:worship-completed` (dispatched by TheDivine on Fuse)
 *       - `divine:profile-updated`
 *   • Exposes:
 *       - blink       : boolean — union of all pending notes (used by tile & phase pill)
 *       - blinkingNoteIds : Set<string> — per-note flicker on/off
 *       - notes       : full status list [{note_id, title, scheduled_time,
 *                         worshipped_today, needs_flicker, ...}]
 */
export const useDivineBlink = () => {
  const [status, setStatus] = useState({ notes: [], needs_worship: false });

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/divine/worship-status`);
      setStatus(data || { notes: [], needs_worship: false });
    } catch {
      setStatus({ notes: [], needs_worship: false });
    }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 20_000);
    const onEvent = () => refresh();
    window.addEventListener('divine:worship-completed', onEvent);
    window.addEventListener('divine:profile-updated', onEvent);
    return () => {
      clearInterval(iv);
      window.removeEventListener('divine:worship-completed', onEvent);
      window.removeEventListener('divine:profile-updated', onEvent);
    };
  }, []);

  const blinkingNoteIds = new Set(
    (status.notes || []).filter(n => n.needs_flicker).map(n => n.note_id)
  );
  const blink = blinkingNoteIds.size > 0;

  return {
    blink,
    blinkingNoteIds,
    notes: status.notes || [],
    needsWorship: !!status.needs_worship,
    refresh,
  };
};
